"use client";
import React from "react";
import { PortableText } from "@portabletext/react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import styles from "./aboutContentSection.module.css";

export type ContentSection = {
  heading: string;
  body: any; // PortableText array
};

export type AboutContentSectionProps = {
  sections?: ContentSection[];
};

const defaultSections: ContentSection[] = [
  {
    heading: "Background and Expertise",
    body: [
      {
        _type: "block",
        _key: "default-1",
        style: "normal",
        children: [
          {
            _type: "span",
            _key: "span-1",
            text: "My work covers front end, backend, mobile, and cloud systems. I have led multiple engineering teams and have helped define stable delivery practices across organizations using Azure DevOps, React, Next.js, .NET, and structured release cycles.",
            marks: [],
          },
        ],
        markDefs: [],
      },
      {
        _type: "block",
        _key: "default-2",
        style: "normal",
        children: [
          {
            _type: "span",
            _key: "span-2",
            text: "I build delivery systems that support clear expectations, early planning, and consistent quality. This includes sprint calendars, refinement rules, story writing, Definition of Ready and Definition of Done, and alignment between development and QA.",
            marks: [],
          },
        ],
        markDefs: [],
      },
      {
        _type: "block",
        _key: "default-3",
        style: "normal",
        children: [
          {
            _type: "span",
            _key: "span-3",
            text: "I support startups and established teams that need predictable release flow and strong onboarding for developers, designers, and QA. My background includes hands-on technical leadership and people management. I also focus on automation, template systems, and AI-backed tools that make engineering teams more efficient.",
            marks: [],
          },
        ],
        markDefs: [],
      },
    ],
  },
];

const AboutContentSection: React.FC<AboutContentSectionProps> = ({
  sections = defaultSections,
}) => {
  const [sectionRef, isVisible] = useScrollAnimation();
  const [hasAnimated, setHasAnimated] = React.useState(false);

  React.useEffect(() => {
    if (isVisible && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [isVisible, hasAnimated]);

  if (!sections || sections.length === 0) {
    return null;
  }

  return (
    <article
      ref={sectionRef as React.RefObject<HTMLElement>}
      className={`w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 ${hasAnimated ? styles.sectionAnimation : ""}`}
      aria-label="About content section"
    >
      <div className="space-y-12 lg:space-y-16">
        {sections.map((section, sectionIndex) => (
          <section
            key={sectionIndex}
            className="space-y-6"
            aria-labelledby={`section-heading-${sectionIndex}`}
          >
            <h2
              id={`section-heading-${sectionIndex}`}
              className={`font-inter font-semibold text-white text-2xl sm:text-3xl lg:text-[32px] tracking-normal leading-8 lg:leading-10 mb-6 ${hasAnimated ? styles.headingAnimation : ""}`}
              style={hasAnimated ? { animationDelay: `${0.1 + sectionIndex * 0.3}s` } : undefined}
            >
              {section.heading}
            </h2>
            <div className={`font-inter font-normal text-slate-300 text-base sm:text-lg lg:text-xl leading-7 lg:leading-8 space-y-4 ${hasAnimated ? styles.contentAnimation : ""}`}
              style={hasAnimated ? { animationDelay: `${0.2 + sectionIndex * 0.3}s` } : undefined}
            >
              <PortableText
                value={section.body}
                components={{
                  block: {
                    normal: ({ children }) => (
                      <p className="mb-4 last:mb-0">{children}</p>
                    ),
                    h2: ({ children }) => (
                      <h2 className="font-semibold text-white text-xl lg:text-2xl mt-6 mb-4">
                        {children}
                      </h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="font-semibold text-white text-lg lg:text-xl mt-4 mb-3">
                        {children}
                      </h3>
                    ),
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-4 border-slate-600 pl-4 italic text-slate-400 my-4">
                        {children}
                      </blockquote>
                    ),
                  },
                  marks: {
                    strong: ({ children }) => (
                      <strong className="font-semibold text-white">
                        {children}
                      </strong>
                    ),
                    em: ({ children }) => (
                      <em className="italic">{children}</em>
                    ),
                    highlight: ({ children }) => (
                      <mark className="bg-[#7f56d9] text-white px-1.5 py-0.5 rounded font-semibold">
                        {children}
                      </mark>
                    ),
                    link: ({ value, children }) => {
                      const target = value?.blank ? "_blank" : undefined;
                      const rel = value?.blank
                        ? "noopener noreferrer"
                        : undefined;
                      return (
                        <a
                          href={value?.href}
                          target={target}
                          rel={rel}
                          className="text-[#a78bfa] hover:text-[#c4b5fd] underline decoration-[#a78bfa]/50 hover:decoration-[#a78bfa] transition-colors duration-200 ease-in-out font-medium"
                        >
                          {children}
                        </a>
                      );
                    },
                  },
                  list: {
                    bullet: ({ children }) => (
                      <ul className="list-disc list-inside space-y-2 my-4 ml-4">
                        {children}
                      </ul>
                    ),
                    number: ({ children }) => (
                      <ol className="list-decimal list-inside space-y-2 my-4 ml-4">
                        {children}
                      </ol>
                    ),
                  },
                  listItem: {
                    bullet: ({ children }) => (
                      <li className="ml-2">{children}</li>
                    ),
                    number: ({ children }) => (
                      <li className="ml-2">{children}</li>
                    ),
                  },
                }}
              />
            </div>
          </section>
        ))}
      </div>
    </article>
  );
};

export default AboutContentSection;

